import { useEffect, useMemo, useState } from "react";
import { useHubSpotProducts } from "../../hooks/useHubSpotProducts";
import {
  fetchProductTranslations,
  saveProductTranslations,
  type ProductTranslations,
} from "../../api/flows";
import { TRANSLATION_LOCALES, type TranslationLocale } from "../../types/configurator";
import { ChevronIcon } from "../ui/Icons";
import styles from "../FlowSelector/FlowSelector.module.css";

const LOCALE_LABELS: Record<TranslationLocale, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  pt: "Português",
};

const inputStyle: React.CSSProperties = {
  padding: '7px 10px',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontFamily: 'var(--font)',
  outline: 'none',
  width: '100%',
};

function countTranslatedLocales(t: ProductTranslations | undefined): number {
  if (!t) return 0;
  return TRANSLATION_LOCALES.reduce(
    (n, loc) => n + ((t[loc]?.name ?? "").trim() !== "" ? 1 : 0),
    0,
  );
}

export function ProductManagement() {
  const { categories, loading, error, search } = useHubSpotProducts();
  const [query, setQuery] = useState("");
  const [translationsMap, setTranslationsMap] = useState<Record<string, ProductTranslations>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchProductTranslations().then(setTranslationsMap).catch(console.error);
  }, []);

  const filtered = useMemo(() => search(query), [query, categories]);
  const sortedCategories = Object.keys(filtered).sort((a, b) => a.localeCompare(b));

  const handleBlur = async (productId: string, locale: TranslationLocale, text: string) => {
    const trimmed = text.trim();
    const current = translationsMap[productId] ?? {};
    if ((current[locale]?.name ?? "") === trimmed) return;

    const next: ProductTranslations = { ...current };
    if (trimmed) next[locale] = { ...(current[locale] ?? {}), name: trimmed };
    else delete next[locale];

    const saveKey = `${productId}:${locale}`;
    setSaving((prev) => ({ ...prev, [saveKey]: true }));
    try {
      await saveProductTranslations(productId, next);
      setTranslationsMap((prev) => {
        const out = { ...prev };
        if (Object.keys(next).length === 0) delete out[productId];
        else out[productId] = next;
        return out;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving((prev) => ({ ...prev, [saveKey]: false }));
    }
  };

  return (
    <div className={styles.screen}>
      <div className={styles.inner} style={{ maxWidth: 860 }}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Product Management</h1>
            <p className={styles.subtitle}>
              Translate product titles. These are shared across all flows.
            </p>
          </div>
          <button
            className="btn"
            onClick={() => { window.location.hash = ''; }}
          >
            ← Back to flows
          </button>
        </div>

        <div
          role="note"
          style={{
            marginBottom: 24,
            padding: '10px 14px',
            fontSize: 13,
            color: 'var(--text-primary)',
            background: 'var(--warm-brown-dim)',
            border: '1px solid rgba(140, 105, 55, 0.3)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          ℹ️ If no translation is added for a language, customers see the product's default name from HubSpot.
          Dutch always uses the HubSpot name.
        </div>

        {loading && (
          <div className={styles.grid}>
            {[0, 1, 2].map((i) => <div key={i} className={styles.skeleton} />)}
          </div>
        )}

        {!loading && error && <p className={styles.empty}>Error loading products: {error}</p>}

        {!loading && !error && (
          <>
            <div style={{ marginBottom: 24 }}>
              <input
                type="search"
                placeholder="Search by name or SKU…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ ...inputStyle, fontSize: 14, padding: '8px 12px', maxWidth: 320 }}
              />
            </div>

            {sortedCategories.length === 0 && (
              <p className={styles.empty}>No products found.</p>
            )}

            {sortedCategories.map((cat) => (
              <section key={cat} style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 8px' }}>
                  {cat}
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[...filtered[cat]].sort((a, b) => a.name.localeCompare(b.name)).map((product) => {
                    const rowTranslations = translationsMap[product.id];
                    const completeCount = countTranslatedLocales(rowTranslations);
                    const allComplete = completeCount === TRANSLATION_LOCALES.length;
                    const isOpen = expanded[product.id] ?? false;

                    return (
                      <div
                        key={product.id}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto auto',
                            alignItems: 'center',
                            gap: 12,
                            padding: '12px 16px',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                              {product.name}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                              {product.sku || product.id}
                            </div>
                          </div>
                          {completeCount > 0 ? (
                            <span
                              title={`${completeCount} of ${TRANSLATION_LOCALES.length} locales translated`}
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid',
                                background: allComplete ? 'rgba(40, 140, 60, 0.12)' : 'rgba(140, 105, 55, 0.15)',
                                color: allComplete ? 'rgb(30, 110, 50)' : 'var(--warm-brown)',
                                borderColor: allComplete ? 'rgba(40, 140, 60, 0.35)' : 'rgba(140, 105, 55, 0.3)',
                              }}
                            >
                              🌐 {completeCount}/{TRANSLATION_LOCALES.length}
                            </span>
                          ) : <span />}
                          <button
                            type="button"
                            onClick={() => setExpanded((prev) => ({ ...prev, [product.id]: !isOpen }))}
                            title={isOpen ? 'Hide translations' : 'Show translations'}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 28,
                              background: 'transparent',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius-sm)',
                              color: 'var(--text-muted)',
                              cursor: 'pointer',
                            }}
                          >
                            <ChevronIcon open={isOpen} />
                          </button>
                        </div>

                        {isOpen && (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'auto 1fr auto',
                              alignItems: 'center',
                              gap: '8px 12px',
                              padding: '12px 16px 14px',
                              borderTop: '1px solid rgba(140, 105, 55, 0.15)',
                              background: 'var(--warm-brown-dim)',
                            }}
                          >
                            {TRANSLATION_LOCALES.map((loc) => {
                              const value = rowTranslations?.[loc]?.name ?? '';
                              return [
                                <label key={`${loc}-label`} htmlFor={`pt-${product.id}-${loc}`} style={{ fontSize: 12, color: 'var(--text-primary)', minWidth: 72 }}>
                                  {LOCALE_LABELS[loc]}
                                </label>,
                                <input
                                  key={`${loc}-${value}`}
                                  id={`pt-${product.id}-${loc}`}
                                  type="text"
                                  placeholder={product.name}
                                  defaultValue={value}
                                  onBlur={(e) => handleBlur(product.id, loc, e.target.value)}
                                  style={inputStyle}
                                />,
                                <span key={`${loc}-status`} style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 48, textAlign: 'right' }}>
                                  {saving[`${product.id}:${loc}`] ? 'saving…' : value ? '✓' : ''}
                                </span>,
                              ];
                            })}
                            <span style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                              Empty translations fall back to the HubSpot name.
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
